import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, tap } from 'rxjs';
import { QuestionnaireResponse } from '../models/questionnaire.model';
import { DonorMetric } from '../models/donor-metric.model';
import { environment } from '../../environments/environment';
import { LocaleService } from './locale.service';

export interface QuestionnaireTimingEvent {
  questionnaireId: string;
  templateId: string;
  sectionId: string | null;
  subsectionId: string | null;
  eventType: string;
  startedAt: string;
  endedAt?: string;
  durationMs?: number;
  locale: string;
}


@Injectable({
  providedIn: 'root'
})
export class QuestionnaireService {
  // private baseUrl = 'http://localhost:3000/api/templates';
  private baseUrl = environment.questionnaireApiUrl;
  private templatesBaseUrl = `${this.baseUrl}/templates`;
  private cacheKeyPrefix = 'questionnaireCache_';


  constructor(
    private http: HttpClient,
    private localeService: LocaleService
  ) {}

  // Fetch questionnaire data by template ID
  getQuestionnaireById(templateId: string, locale = this.localeService.currentLocale): Observable<QuestionnaireResponse> {
    const cacheKey = `${this.cacheKeyPrefix}${templateId}_${locale}`;
    const cachedData = this.getCachedData(cacheKey);

    if (cachedData) {
      return of(cachedData); // Return cached data as an observable
    }
    const url = `${this.templatesBaseUrl}/${templateId}?locale=${encodeURIComponent(locale)}`;
    // Retrieve tokens from localStorage

    return this.http.get<QuestionnaireResponse>(url).pipe(
      tap((response) => {
        this.cacheData(cacheKey, response, 30 * 60 * 1000); // Cache for 30 minutes
      }));
  }

  getQuestionnaireByName(templateName: string, locale = this.localeService.currentLocale): Observable<QuestionnaireResponse> {
    const cacheKey = `${this.cacheKeyPrefix}${templateName}_${locale}`;
    const cachedData = this.getCachedData(cacheKey);

    if (cachedData) {
      return of(cachedData); // Return cached data as an observable
    }
    const url = `${this.templatesBaseUrl}/name/${templateName}?activeOnly=true&locale=${encodeURIComponent(locale)}`;
    // Retrieve tokens from localStorage

    return this.http.get<QuestionnaireResponse>(url).pipe(
      tap((response) => {
        this.cacheData(cacheKey, response, 30 * 60 * 1000); // Cache for 30 minutes
      }));
  }

  checkSubsectionVisibility(subsectionId: string) {
    const url = `${this.templatesBaseUrl}/subsections/${subsectionId}/visibility`;
    return this.http.get<{ visible: boolean }>(url);
  }

  getDonorMetrics(userId: string): Observable<DonorMetric[]> {
    // const cacheKey = `donorMetrics_${userId}`;
    // const cachedData = this.getCachedData(cacheKey);

    // if (cachedData) {
    //   return of(cachedData); // Return cached data as an observable
    // }
    const url = `${this.baseUrl}/metrics/${userId}`;
    return this.http.get<DonorMetric[]>(url);
    // return this.http.get<DonorMetric[]>(url).pipe(
    //   tap((response) => {
    //     this.cacheData(cacheKey, response, 300000); // Cache for 5 minutes
    //   }));
  }

  getDonorMetricsBulk(userIds: string[]): Observable<Record<string, DonorMetric[]>> {
    const url = `${this.baseUrl}/metrics/bulk`;
    return this.http.post<Record<string, DonorMetric[]>>(url, { userIds });
  }

  recordQuestionnaireTiming(payload: QuestionnaireTimingEvent): Observable<unknown> {
    const url = `${this.baseUrl}/questionnaire-timing`;
    return this.http.post(url, payload);
  }

  resetQuestionnaireByUserId(userId: string){
    const url = `${this.baseUrl}/admin/responses/reset/${userId}`;
    return this.http.post(url, {});
  }

  // Cache data with a timestamp
  private cacheData(key: string, data: any, ttl: number): void {
    const cacheEntry = {
      data,
      expiry: Date.now() + ttl, // Set expiration timestamp
    };
    localStorage.setItem(key, JSON.stringify(cacheEntry));
  }

  // Retrieve cached data if it's not expired
  private getCachedData(key: string): any {
    const cacheEntryString = localStorage.getItem(key);
    if (!cacheEntryString) {
      return null; // No cache entry
    }

    const cacheEntry = JSON.parse(cacheEntryString);
    if (Date.now() > cacheEntry.expiry) {
      localStorage.removeItem(key); // Remove expired cache
      return null;
    }

    return cacheEntry.data;
  }
}
